interface TimeDataReturnValue {
  data: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  description: string;
}

/**
 * Converts a given time in seconds to an object containing days, hours, minutes, and seconds.
 * Also generates a human-readable description string.
 *
 * @param timeInSeconds - The time duration in seconds.
 * @param dayUnitString - The string to use for the day unit (default is 'day').
 * @param hourUnitString - The string to use for the hour unit (default is 'hour').
 * @param minuteUnitString - The string to use for the minute unit (default is 'min').
 * @returns An object containing:
 *  - `data`: An object with the properties `days`, `hours`, `minutes`, and `seconds`.
 *  - `description`: A human-readable string describing the time duration.
 */
export const getTimeDataFromSeconds = (
  timeInSeconds: number,
  dayUnitString: string = 'day',
  hourUnitString: string = 'hour',
  minuteUnitString: string = 'min',
): TimeDataReturnValue => {
  const days = timeInSeconds / (60 * 60 * 24);
  const hours = (days - Math.floor(days)) * 24;
  const minutes = (hours - Math.floor(hours)) * 60;
  const seconds = (minutes - Math.floor(minutes)) * 60;

  const data = {
    days: Math.floor(days),
    hours: Math.floor(hours),
    minutes: Math.floor(minutes),
    seconds: Math.round(seconds),
  };

  const dayString =
    data.days > 0
      ? `${data.days === 1 ? `1 ${dayUnitString}` : `${data.days} ${dayUnitString}s`} `
      : '';
  const hourString =
    data.hours > 0
      ? `${data.hours === 1 ? `1 ${hourUnitString}` : `${data.hours} ${hourUnitString}s`} `
      : '';
  const minuteString =
    data.minutes > 0
      ? `${data.minutes === 1 ? `1 ${minuteUnitString}` : `${data.minutes} ${minuteUnitString}s`}`
      : '';

  const description = `${dayString}${hourString}${
    data.days > 0 ? '' : minuteString
  }`.trim();

  return {
    data,
    description,
  };
};
